/**
 * Teto de desconto de um item do catálogo — espelho da regra do backend
 * (`apps/backend/src/modules/medical/catalog/catalog-max-discount.util.ts`).
 *
 * REGRA (2026-08-06): item **sem teto configurado não aceita desconto**. Antes,
 * `maxDiscount` nulo valia como "sem limite" e a tela liberava até 100%.
 *
 * O teto segue a MESMA cascata do preço (parceiro > profissional > filial): se o
 * preço cobrado veio do parceiro, é o limite dele que vale.
 *
 * Manter as duas implementações em sincronia: a daqui decide o que a tela
 * permite digitar, a do backend é quem de fato recusa.
 */
import { Catalog, CatalogVariation, catalogCompanyPrice } from './catalog-api'

/** Sem teto configurado = nenhum desconto permitido. */
export const NO_DISCOUNT_ALLOWED = 0

/** Teto de UM item, pela cascata parceiro > profissional > filial. */
export function cascadeMaxDiscount(
    catalog: Pick<Catalog, 'partners' | 'professionals' | 'companyPrices'>,
    ctx: {
        companyId?: number | null
        partnerId?: number | null
        professionalId?: number | null
    },
): number {
    const partnerRow = ctx.partnerId
        ? (catalog.partners || []).find(p => p.partnerId === ctx.partnerId)
        : undefined
    if (partnerRow) return partnerRow.maxDiscount ?? NO_DISCOUNT_ALLOWED

    const professionalRow = ctx.professionalId
        ? (catalog.professionals || []).find(
              p => p.professionalId === ctx.professionalId,
          )
        : undefined
    if (professionalRow) {
        return professionalRow.maxDiscount ?? NO_DISCOUNT_ALLOWED
    }

    return (
        catalogCompanyPrice(catalog, ctx.companyId).maxDiscount ??
        NO_DISCOUNT_ALLOWED
    )
}

/**
 * Teto de uma VARIAÇÃO, pela mesma cascata. A variação é um Catalog filho com
 * parceiros/profissionais próprios — ver `resolveVariationsForPricing`.
 */
export function variationMaxDiscount(
    variation: CatalogVariation,
    ctx: {
        companyId?: number | null
        partnerId?: number | null
        professionalId?: number | null
    },
): number {
    const partnerRow = ctx.partnerId
        ? (variation.partners || []).find(p => p.partnerId === ctx.partnerId)
        : undefined
    if (partnerRow) return partnerRow.maxDiscount ?? NO_DISCOUNT_ALLOWED

    const professionalRow = ctx.professionalId
        ? (variation.professionals || []).find(
              p => p.professionalId === ctx.professionalId,
          )
        : undefined
    if (professionalRow) {
        return professionalRow.maxDiscount ?? NO_DISCOUNT_ALLOWED
    }

    const list = variation.companyPrices || []
    const cp =
        list.find(p => p.companyId === ctx.companyId) ??
        (ctx.companyId == null ? list[0] : undefined)
    return cp?.maxDiscount ?? NO_DISCOUNT_ALLOWED
}

/**
 * Teto do atendimento a partir dos tetos de cada item: vale o MENOR, e um único
 * item sem teto (0) zera o desconto do atendimento inteiro. Lista vazia = 0.
 */
export function combineMaxDiscounts(
    values: Array<number | null | undefined>,
): number {
    if (!values.length) return NO_DISCOUNT_ALLOWED
    return Math.min(...values.map(v => v ?? NO_DISCOUNT_ALLOWED))
}
